import { motion } from "framer-motion";
import { BadgeCheck, HelpCircle, Sparkles, TrendingUp } from "lucide-react";
import { PHOTOS } from "./data";

const ease = [0.16, 1, 0.3, 1];

const SIGNS = [
  "Refuse or struggle to begin tasks?",
  "Call out, interrupt or leave their seats?",
  "Display unmanageable behaviour?",
  "Lose focus easily?",
  "Struggle to follow instructions?",
  "Find it difficult to communicate or work with others?",
];

const STEPS = [
  { n: "1", t: "Establish a Baseline", d: "A clear starting point for your school." },
  { n: "2", t: "Build Teacher Capability", d: "Practical skills and confidence for staff." },
  { n: "3", t: "Develop Inclusive Systems", d: "Structures that make inclusion routine." },
  { n: "4", t: "Practise Inclusive Teaching", d: "Strategies embedded in the classroom." },
  { n: "5", t: "Impact Review", d: "Evidence of what's working, and what's next." },
  { n: "6", t: "Sustained Inclusive Growth", d: "Change that lasts beyond our visit." },
];

const HIGHLIGHTS = [
  { icon: BadgeCheck, label: "Evidence-informed" },
  { icon: Sparkles, label: "Whole-school" },
  { icon: TrendingUp, label: "Built for lasting change" },
];

export const InclusionSupport = () => (
  <section id="inclusion-support" className="relative py-24 md:py-36 overflow-hidden" data-testid="inclusion-support">
    <div className="noise-overlay" />
    <div aria-hidden="true" className="absolute -top-20 -right-20 w-72 h-72 border border-terra/20 rotate-12" />
    <div aria-hidden="true" className="absolute -bottom-24 -left-24 w-80 h-80 bg-sand/70 rounded-full" />

    <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
      <div className="mb-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:items-end">
        <div className="lg:col-span-8 max-w-3xl">
          <p className="text-sm md:text-base tracking-[0.22em] uppercase font-semibold text-terra mb-3">
            What we do
          </p>
          <h2 className="font-serif text-5xl md:text-7xl leading-[0.98] tracking-tight">
            Strategic Whole School<br />Inclusion Support<span className="text-terra">.</span>
          </h2>
          <p className="mt-3 font-serif text-xl md:text-2xl italic text-terra">
            Stronger teaching. Inclusive schools. Better outcomes for all.
          </p>
          <p className="mt-6 text-ink/65 md:text-lg leading-relaxed">
            This service is designed to build an{" "}
            <span
              tabIndex={0}
              aria-describedby="inclusive-learning-definition"
              data-testid="inclusive-learning-term"
              className="relative inline-block group/inclusive mx-1 cursor-help border-b border-dashed border-terra text-terra font-semibold outline-none"
            >
              inclusive learning
              <span
                id="inclusive-learning-definition"
                role="tooltip"
                data-testid="inclusive-learning-definition"
                className="pointer-events-none absolute left-0 top-full z-30 mt-3 w-80 max-w-[calc(100vw-3rem)] -translate-y-1 bg-ink px-4 py-3 text-left text-sm font-normal leading-relaxed text-bone opacity-0 invisible shadow-2xl border border-terra/25 transition-all duration-300 group-hover/inclusive:visible group-hover/inclusive:translate-y-0 group-hover/inclusive:opacity-100 group-focus-within/inclusive:visible group-focus-within/inclusive:translate-y-0 group-focus-within/inclusive:opacity-100"
              >
                an approach that promotes equal access to high-quality education by removing barriers and providing
                necessary support to different learning needs, including those with special needs.
              </span>
            </span>{" "}
            culture at your school through strategic support and evidence-informed, practical solutions.
          </p>
        </div>
        <div className="lg:col-span-4 flex flex-wrap gap-2.5 lg:justify-end" data-testid="inclusion-highlights">
          {HIGHLIGHTS.map(({ icon: Icon, label }) => (
            <span key={label} className="inline-flex items-center gap-2 border border-ink/15 bg-bone px-3.5 py-2 text-xs font-semibold tracking-wide text-ink/75">
              <Icon className="w-4 h-4 text-terra" /> {label}
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
        <motion.figure
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease }}
          className="lg:col-span-4 relative self-start"
        >
          <div aria-hidden="true" className="absolute -left-3 -top-3 h-full w-full border border-terra/30" />
          <div className="relative aspect-[4/3] overflow-hidden bg-pine">
            <img
              src={PHOTOS.inclusion}
              alt="JD Sir leading an inclusion support session with educators"
              data-testid="inclusion-photo"
              className="h-full w-full object-cover object-center"
            />
            <div className="absolute inset-x-0 bottom-0 bg-pine/90 px-5 py-4 text-bone">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-terra">Inclusion in practice</p>
              <p className="mt-1 text-sm text-bone/85">Strategic support, live with your school community.</p>
            </div>
          </div>
        </motion.figure>

        <div className="lg:col-span-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease, delay: 0.08 }}
            className="bg-sand/70 border border-ink/10 p-7 md:p-10 relative overflow-hidden"
            data-testid="inclusion-signs"
          >
            <div aria-hidden="true" className="absolute right-0 top-0 h-28 w-28 bg-terra/10 rounded-bl-full" />
            <div className="relative">
              <span className="inline-flex items-center gap-2 bg-pine text-bone px-3 py-1.5 text-xs font-semibold tracking-[0.16em] uppercase">
                <HelpCircle className="w-4 h-4 text-terra" /> Spot the signs early
              </span>
              <p className="mt-6 font-serif text-2xl md:text-3xl">Do your students regularly…</p>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {SIGNS.map((s) => (
                  <li key={s} className="flex items-start gap-3 text-ink/75 md:text-lg">
                    <span className="mt-0.5 w-7 h-7 shrink-0 bg-pine flex items-center justify-center">
                      <HelpCircle className="w-4 h-4 text-terra" />
                    </span>
                    {s}
                  </li>
                ))}
              </ul>
              <p className="mt-7 border-l-4 border-terra pl-4 font-bold text-ink text-sm md:text-base leading-relaxed">
                These behaviours may reflect unmet learning, communication, sensory or emotional needs. If that sounds
                familiar, we'd love to support you and your team.
              </p>
            </div>
          </motion.div>

          <div className="mt-8 md:mt-10">
            <div className="mb-5 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
              <p className="font-serif text-2xl md:text-3xl text-terra">The Support Process</p>
              <p className="text-sm text-ink/55 max-w-md">A practical six-step route from first insight to sustained inclusive growth.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 border-t border-l border-ink/15" data-testid="inclusion-process">
              {STEPS.map((s, i) => (
                <motion.div
                  key={s.n}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, ease, delay: (i % 2) * 0.06 }}
                  className="relative border-r border-b border-ink/15 bg-bone/55 p-5 md:p-6 group hover:bg-pine transition-colors duration-300"
                >
                  <span className="font-serif text-4xl text-ink/15 group-hover:text-terra transition-colors">{s.n}</span>
                  <p className="font-serif text-xl mt-2 group-hover:text-bone transition-colors">{s.t}</p>
                  <p className="text-ink/60 text-sm mt-1 group-hover:text-bone/70 transition-colors">{s.d}</p>
                </motion.div>
              ))}
            </div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease, delay: 0.1 }}
              data-testid="inclusion-current-schools"
              className="mt-6 border-l-4 border-terra bg-sand/60 px-5 py-4 text-sm md:text-base text-ink/70"
            >
              Two schools are currently availing this service over the duration of this academic year.
            </motion.p>
          </div>
        </div>
      </div>
    </div>
  </section>
);
